/**
 * Heavy element registrations — Monaco editor / QR code / document validator.
 *
 * These pull the ~396 KB Monaco graph and are unused on most routes, so they
 * live outside the eager `defineAllRest()` set. The define is triggered lazily
 * once a page actually renders one of the tags (see `econnect.client.ts`).
 *
 * `withoutMonacoPrewarm` strips `requestIdleCallback` across the `ui-views`
 * import so its module-level Monaco pre-warm (~830 KiB of CDN scripts) is
 * skipped; real `<e-editor>` mounts still load Monaco on demand.
 *
 * `ensureHeavyDefines` is a global idempotent latch — custom-element
 * registrations are permanent, so a single load covers every later navigation.
 * The `heavyLoaded` flag returns early only once the define has actually run;
 * a DOM-presence miss does NOT set the latch, so later callers retry.
 */
export function withoutMonacoPrewarm<T>(work: () => Promise<T>): Promise<T> {
  if (typeof globalThis === 'undefined') return work();
  const g = globalThis as unknown as { requestIdleCallback?: typeof window.requestIdleCallback };
  const original = g.requestIdleCallback;
  if (original === undefined) return work();
  delete g.requestIdleCallback;
  return work().finally(() => {
    g.requestIdleCallback = original;
  });
}

let heavyLoaded = false;

/**
 * Register the heavy elements if any are present in the DOM. Safe to call
 * repeatedly and from anywhere (plugin DOM-poll, or a component's `onMounted`
 * where the element is guaranteed to exist).
 */
export async function ensureHeavyDefines(): Promise<void> {
  if (heavyLoaded) return;
  if (!document.querySelector('e-editor,e-richtext-editor,e-qr-code,e-document-validator')) return;
  heavyLoaded = true;
  await withoutMonacoPrewarm(async () => {
    const { defineEditor, defineQrCode, defineDocumentValidator } = await import(
      '@econnect/ui-views/define'
    );
    defineEditor();
    defineQrCode();
    defineDocumentValidator();
  });
}
